import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FormField } from './forms';

export type ComboboxOption = { value: string; label: string; sublabel?: string };

export function SearchableSelect({
  label, value, onChange, options, placeholder = 'Search…', help, error, id: suppliedId,
}: {
  label: string; value: string; onChange: (value: string) => void; options: ComboboxOption[];
  placeholder?: string; help?: string; error?: string; id?: string;
}) {
  const generatedId = useId();
  const id = suppliedId ?? generatedId;
  const listboxId = `${id}-listbox`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((option) => option.value === value);
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => `${option.label} ${option.sublabel ?? ''}`.toLowerCase().includes(term));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const openList = () => { setOpen(true); setQuery(''); setActiveIndex(0); };
  const choose = (option: ComboboxOption) => { onChange(option.value); setOpen(false); setQuery(''); inputRef.current?.blur(); };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') { event.preventDefault(); if (!open) openList(); else setActiveIndex((index) => Math.min(index + 1, filtered.length - 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActiveIndex((index) => Math.max(index - 1, 0)); }
    else if (event.key === 'Enter') { event.preventDefault(); const option = filtered[activeIndex]; if (option) choose(option); }
    else if (event.key === 'Escape') { setOpen(false); setQuery(''); }
  };

  return <FormField label={label} htmlFor={id} help={help} error={error}>
    <div className="bv-combobox" ref={rootRef}>
      <div className="bv-combobox__control">
        <input
          id={id} ref={inputRef} className="bv-input bv-combobox__input" role="combobox"
          aria-expanded={open} aria-controls={listboxId} aria-autocomplete="list" autoComplete="off"
          value={open ? query : (selected?.label ?? '')}
          placeholder={selected ? selected.label : placeholder}
          onChange={(event) => { setQuery(event.target.value); if (!open) setOpen(true); setActiveIndex(0); }}
          onFocus={openList}
          // Focus opens the list, but after Escape the input keeps focus, so a click has to reopen it. The input
          // fills the control and the chevron ignores pointer events, so every click lands here.
          onClick={() => { if (!open) openList(); }}
          onKeyDown={onKeyDown}
          aria-invalid={Boolean(error)}
        />
        <ChevronDown aria-hidden="true" size={16} className="bv-combobox__chevron" />
      </div>
      {open && <ul className="bv-combobox__listbox" id={listboxId} role="listbox">
        {filtered.length === 0 && <li className="bv-combobox__empty">No matches</li>}
        {filtered.map((option, index) => (
          <li key={option.value} role="option" aria-selected={option.value === value}
            className={`bv-combobox__option${index === activeIndex ? ' bv-combobox__option--active' : ''}`}
            onMouseEnter={() => setActiveIndex(index)} onMouseDown={(event) => { event.preventDefault(); choose(option); }}>
            <span>{option.label}{option.sublabel && <span className="bv-combobox__sublabel"> {option.sublabel}</span>}</span>
            {option.value === value && <Check aria-hidden="true" size={16} />}
          </li>
        ))}
      </ul>}
    </div>
  </FormField>;
}
